import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useRecentlyViewedStore } from "@/stores/recentlyViewedStore";
import { fetchProductByHandle, ShopifyProduct } from "@/lib/shopify";
import { ProductCard } from "@/components/ProductCard";

interface RecentlyViewedSectionProps {
  currentHandle: string;
}

export const RecentlyViewedSection = ({ currentHandle }: RecentlyViewedSectionProps) => {
  const handles = useRecentlyViewedStore((s) => s.handles);
  const [products, setProducts] = useState<ShopifyProduct[]>([]);

  const filteredHandles = handles.filter((h) => h !== currentHandle).slice(0, 8);

  useEffect(() => {
    if (filteredHandles.length === 0) return;
    let cancelled = false;
    const load = async () => {
      const results = await Promise.all(
        filteredHandles.map(async (h) => {
          try {
            const p = await fetchProductByHandle(h);
            return p ? ({ node: p } as ShopifyProduct) : null;
          } catch {
            return null;
          }
        })
      );
      if (!cancelled) setProducts(results.filter(Boolean) as ShopifyProduct[]);
    };
    load();
    return () => { cancelled = true; };
  }, [filteredHandles.join(",")]);

  if (products.length === 0) return null;

  return (
    <section className="border-t border-border py-10 md:py-14">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-sans text-[22px] md:text-[26px] font-semibold text-foreground">
            Recently Viewed
          </h2>
          <div className="hidden md:flex gap-2">
            <button
              onClick={() => document.getElementById("rv-carousel")?.scrollBy({ left: -280, behavior: "smooth" })}
              className="w-10 h-10 rounded-full border border-border flex items-center justify-center hover:border-foreground/40 transition-colors"
            >
              <ChevronLeft className="h-4 w-4 text-foreground" />
            </button>
            <button
              onClick={() => document.getElementById("rv-carousel")?.scrollBy({ left: 280, behavior: "smooth" })}
              className="w-10 h-10 rounded-full border border-border flex items-center justify-center hover:border-foreground/40 transition-colors"
            >
              <ChevronRight className="h-4 w-4 text-foreground" />
            </button>
          </div>
        </div>
        <div id="rv-carousel" className="flex gap-4 overflow-x-auto pb-4 scrollbar-hide">
          {products.map((p) => (
            <div key={p.node.id} className="flex-shrink-0 w-[200px] md:w-[220px]">
              <ProductCard product={p} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
